import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { isModKey } from '../utils/keyboard';
import Environment from './Environment';
import EnvironmentEditor from './EnvironmentEditor';

/**
 * Large two-pane modal for managing variable scopes:
 *  - Header: title + close.
 *  - Left: environment sidebar (Globals + environments list).
 *  - Right: the EnvironmentEditor for the current selection.
 *  - Footer: Save / Save & Close.
 */
const EnvironmentsModal = ({
  isOpen,
  onClose,
  environments = [],
  selectedEnvironment,
  globalVariables = [],
  workspaceVariables = [],
  onEnvironmentsChange,
  onSelectedEnvironmentChange,
  onSaveScope,
  currentWorkspaceId = null,
  onVariableUpdate,
}) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState({ type: 'global' });
  // The currently mounted editor registers its save handler here.
  const saveRef = useRef(null);

  // On open, focus the active environment if there is one; otherwise globals.
  useEffect(() => {
    if (!isOpen) return;
    if (selectedEnvironment?.id && environments.some((e) => e.id === selectedEnvironment.id)) {
      setSelection({ type: 'environment', envId: selectedEnvironment.id });
    } else {
      setSelection({ type: 'global' });
    }
    // Only re-run when the modal opens — don't yank the pane while it's open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Escape / Ctrl+S while open — capture phase so request save shortcuts don't fire behind the modal.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (isModKey(e) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
        saveRef.current?.();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  // If the environment being edited disappears (deleted), fall back to globals.
  useEffect(() => {
    if (
      selection.type === 'environment' &&
      !selection.draft &&
      !environments.some((env) => env.id === selection.envId)
    ) {
      setSelection({ type: 'global' });
    }
  }, [environments, selection]);

  const handleCreateEnvironment = useCallback(() => {
    setSelection({ type: 'environment', envId: null, draft: true });
  }, []);

  const handleSelectEnvironment = useCallback((env) => {
    setSelection({ type: 'environment', envId: env.id });
  }, []);

  const handleSaveScope = useCallback(
    async (data) => {
      const saved = await onSaveScope?.(data);
      // After creating a brand-new environment from a draft, lock the right
      // pane onto the freshly created record so further edits update it.
      if (selection.draft && saved?.id) {
        setSelection({ type: 'environment', envId: saved.id });
      }
      return saved;
    },
    [onSaveScope, selection.draft]
  );

  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const saveTimerRef = useRef(null);

  const registerSave = useCallback((fn) => {
    saveRef.current = fn;
  }, []);

  const handleSave = useCallback(async () => {
    if (saveState !== 'idle') return;
    setSaveState('saving');
    clearTimeout(saveTimerRef.current);
    const startedAt = Date.now();
    // false = nothing was saved (an empty name, or Plans v2 refused a new
    // environment past the cap): back to "Save", never "Saved".
    const ok = await saveRef.current?.();
    if (ok === false) {
      setSaveState('idle');
      return;
    }
    saveTimerRef.current = setTimeout(() => {
      setSaveState('saved');
      saveTimerRef.current = setTimeout(() => setSaveState('idle'), 1200);
    }, Math.max(0, 500 - (Date.now() - startedAt)));
  }, [saveState]);

const editorProps = useMemo(() => {
    if (selection.type === 'global') {
      return {
        key: 'global',
        isGlobal: true,
        initialData: { name: 'Global Variables', variables: globalVariables },
      };
    }
    if (selection.type === 'workspace') {
      return {
        key: 'workspace',
        isWorkspaceVariables: true,
        initialData: { name: 'Workspace Variables', variables: workspaceVariables },
      };
    }
    if (selection.draft) {
      return {
        key: 'env-draft',
        initialData: { name: '', variables: [] },
      };
    }
    const env = environments.find((e) => e.id === selection.envId);
    return {
      key: `env-${selection.envId}`,
      initialData: env
        ? { id: env.id, name: env.name, variables: env.variables, originalId: env.id }
        : { name: '', variables: [] },
    };
  }, [selection, environments, globalVariables, workspaceVariables]);

  if (!isOpen) return null;

  // React 19 disallows spreading a `key`; pass it explicitly.
  const { key: editorKey, ...editorRest } = editorProps;

  return (
    <div className="env-modal-overlay" onClick={onClose}>
      <div
        className="env-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('environment.manageTitle')}
      >
        <div className="env-modal-body">
          <div className="env-modal-left">
            <Environment
              environments={environments}
              selectedEnvironment={selectedEnvironment}
              globalVariables={globalVariables}
              workspaceVariables={workspaceVariables}
              onEnvironmentsChange={onEnvironmentsChange}
              onSelectedEnvironmentChange={onSelectedEnvironmentChange}
              activeSelection={selection}
              onSelectGlobal={() => setSelection({ type: 'global' })}
              onSelectWorkspace={() => setSelection({ type: 'workspace' })}
              onSelectEnvironment={handleSelectEnvironment}
              onCreateEnvironment={handleCreateEnvironment}
            />
          </div>
          <div className="env-modal-right">
            <EnvironmentEditor
              key={editorKey}
              {...editorRest}
              onSave={handleSaveScope}
              onRegisterSave={registerSave}
              selectedEnvironment={selectedEnvironment}
              workspaceVariables={workspaceVariables}
              globalVariables={globalVariables}
              collectionVariables={[]}
              currentWorkspaceId={currentWorkspaceId}
              onVariableUpdate={onVariableUpdate}
            />
            <div className="env-modal-right-footer">
              <button type="button" className="env-modal-btn" onClick={onClose}>
                {t('common.close')}
              </button>
<button
                type="button"
                className="env-modal-btn env-modal-btn--primary"
                onClick={handleSave}
                disabled={saveState !== 'idle'}
                style={{ minWidth: 72 }}
              >
                {saveState === 'saving' && <span className="env-save-spinner" />}
                {saveState === 'saved' && <Check size={15} color="#4ade80" strokeWidth={2.5} />}
                {saveState === 'idle' && t('common.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentsModal;
